// Single condition row — Polish B2 / v1 layout.
//
// Renders only the right-hand cells: field combo, operator combo,
// value picker, delete icon. The left-hand "Where" / "OR" / "AND"
// gutter is owned by ``FilterGroup`` so it can keep the group
// operator toggle in sync across rows.

import { Trash2 } from "lucide-react";

import { FIELD_REGISTRY, getField, isMultiValue } from "./fieldRegistry";
import { OPERATOR_LABELS, type UiOperator } from "./operators";
import type { FilterCondition as FilterConditionState } from "./treeCodec";
import { ValuePicker } from "./ValuePicker";


export interface FilterConditionRowProps {
  condition: FilterConditionState;
  onChange: (next: FilterConditionState) => void;
  onRemove: () => void;
}


export function FilterConditionRow({
  condition, onChange, onRemove,
}: FilterConditionRowProps) {
  const field = getField(condition.field);
  return (
    <div
      data-testid={`filter-condition-${condition.id}`}
      className="flex flex-wrap items-center gap-2"
    >
      <select
        data-testid={`filter-condition-field-${condition.id}`}
        value={condition.field}
        onChange={(e) => {
          const nextField = getField(e.target.value);
          if (!nextField) {
            onChange({
              ...condition, field: e.target.value, value: "",
            });
            return;
          }
          // Reset operator + value when the field changes; mirrors v1.
          onChange({
            ...condition,
            field: nextField.id,
            operator: nextField.operators[0],
            value: defaultValueFor(nextField.operators[0], nextField),
          });
        }}
        className="min-w-[8rem] rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary"
      >
        <option value="">Field…</option>
        {FIELD_REGISTRY.map((f) => (
          <option key={f.id} value={f.id}>{f.label}</option>
        ))}
      </select>

      <select
        data-testid={`filter-condition-operator-${condition.id}`}
        value={condition.operator}
        disabled={!field}
        onChange={(e) =>
          onChange({
            ...condition,
            operator: e.target.value as UiOperator,
            value: defaultValueFor(e.target.value as UiOperator, field),
          })
        }
        className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary disabled:opacity-50"
      >
        {(field?.operators ?? []).map((op) => (
          <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
        ))}
      </select>

      {field && (
        <ValuePicker
          field={field}
          value={condition.value}
          multi={
            isMultiValue(field.valueType)
            && condition.operator !== "is_empty"
            && condition.operator !== "is_not_empty"
          }
          disabled={
            condition.operator === "is_empty"
            || condition.operator === "is_not_empty"
          }
          onChange={(value) => onChange({ ...condition, value })}
          testId={`filter-condition-value-${condition.id}`}
        />
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove condition"
        data-testid={`filter-condition-remove-${condition.id}`}
        className="ml-auto rounded p-1 text-ember/70 hover:text-ember"
      >
        <Trash2 size={14} aria-hidden="true" />
      </button>
    </div>
  );
}


function defaultValueFor(
  op: UiOperator,
  field: ReturnType<typeof getField> | undefined,
): unknown {
  if (op === "is_empty" || op === "is_not_empty") return "";
  if (field && isMultiValue(field.valueType)) return [];
  return "";
}
